'use client';

import HoneypotField from '@/components/Form/HoneypotField';
import { createEmailValidator } from '@/components/Form/helper/schemaBuilders';
import Label from '@/components/Label/Label';
import { InlineAlert } from '@/components/alert';
import { Button } from '@/components/button';
import BulletpointsField from '@/components/customFields/BulletpointsField';
import { type JSONObject } from '@/components/customFields/types';
import { FormControl } from '@/components/field';
import InlineTextLink from '@/components/inlineTextLink/InlineTextLink';
import { MarketingOptInInfoIconModal } from '@/components/marketingOptInInfoIconModal/MarketingOptInInfoIconModal';
import { RichText } from '@/components/richText/RichText';
import { FormSelect } from '@/components/swdSelect';
import { TextField } from '@/components/textField';
import { richtextToString } from '@/helper/RichText';
import { acceptMarketingOptIn } from '@/helper/cdcOptIn';
import { isHoneypotFilled } from '@/helper/honeypot';
import { postNewsletter } from '@/helper/newsletter';
import { trimStringValues } from '@/helper/utils';
import { useSettings } from '@/providers/SettingsProvider';
import { useUserStore } from '@/providers/UserStoreProvider';
import MailOutlineFilledIcon from '@/public/icons/mail-outline-filled.svg';
import { yupLocales } from '@/public/locales/de/yupLocales';
import type {
  GlobalNewsletterTileStoryblok,
  RichtextNodeStoryblok,
  RichtextStoryblok,
} from '@/types/component-types-sb';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import classes from './NewsletterTile.module.scss';

const EMAIL_PLACEHOLDER = '{emailAddress}';

function withBoldEmailInRichtext(text: RichtextStoryblok | undefined, email: string): RichtextStoryblok | undefined {
  if (!text) {
    return text;
  }

  const transformNode = (node: RichtextNodeStoryblok): RichtextNodeStoryblok | RichtextNodeStoryblok[] => {
    if (node.type === 'text' && node.text.includes(EMAIL_PLACEHOLDER)) {
      const parts = node.text.split(EMAIL_PLACEHOLDER);
      const nodes: RichtextNodeStoryblok[] = [];

      parts.forEach((part, index) => {
        if (part) {
          nodes.push({ type: 'text', text: part, marks: node.marks });
        }

        if (index < parts.length - 1) {
          nodes.push({
            type: 'text',
            text: email,
            marks: [...(node.marks ?? []), { type: 'bold' }],
          });
        }
      });

      return nodes;
    }

    if ('content' in node && node.content) {
      return {
        ...node,
        content: node.content.flatMap((child) => {
          const result = transformNode(child);
          return Array.isArray(result) ? result : [result];
        }),
      };
    }

    return node;
  };

  return {
    ...text,
    content: text.content.flatMap((child) => {
      const result = transformNode(child);
      return Array.isArray(result) ? result : [result];
    }),
  };
}

const NewsletterTile: React.FC<{ blok: GlobalNewsletterTileStoryblok }> = ({ blok }) => {
  const [successful, setSuccessful] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const settings = useSettings();
  const { t } = useTranslation();

  const schema = yup.object({
    email: createEmailValidator('E-Mail-Adresse fehlerhaft').required('E-Mail-Adresse fehlerhaft'),
    salutation: yup.string().optional(),
    firstName: yup.string().optional(),
    lastName: yup.string().optional(),
  });

  const HONEYPOT_FIELD = 'birthdate';

  const { email, loggedIn, hydrated } = useUserStore((state) => ({
    loggedIn: state.loggedIn,
    email: state.email,
    hydrated: state.hydrated,
  }));

  const isLoggedIn = loggedIn;
  const initialEmail = email ?? '';
  const isEmailLocked = isLoggedIn && Boolean(initialEmail);

  // Set german error messages
  yup.setLocale(yupLocales);

  const resetToInitial = (): void => {
    setSuccessful(false);
    setSubmittedEmail('');
    formik.resetForm();
  };

  const formik = useFormik({
    // if there is already something in the user store, use it as initial value
    initialValues: {
      email: initialEmail,
      salutation: '',
      firstName: '',
      lastName: '',
      birthdate: '',
    },
    validationSchema: schema,
    onSubmit: (values, { setSubmitting }) => {
      // Prevent submitting the form in between
      setSubmitting(false);
      setSuccessful(false);
      setSubmittedEmail('');

      values = trimStringValues(values);
      setSubmittedEmail(values.email);

      if (isHoneypotFilled(values as Record<string, unknown>, HONEYPOT_FIELD)) {
        setSuccessful(true);
        return;
      }

      const eventName = 'order-marketing_opt_in_user_finished';
      const handleSuccess = () => {
        setSuccessful(true);
      };
      document.addEventListener(eventName, handleSuccess, { once: true });

      acceptMarketingOptIn(
        values.email,
        '', // title
        values.firstName, // firstname
        values.lastName, // lastname
        values.salutation, // salutation
        richtextToString(settings?.cdc_marketing_optin_text),
        true,
        eventName,
        blok.addtionalData
      ).catch((err) => {
        console.error(err);
        document.removeEventListener(eventName, handleSuccess);
        setSuccessful(false);
        setSubmittedEmail('');
      });

      if (blok.rest_api_endpoint) {
        const data: JSONObject = {
          formId: blok.formId,
          timeStamp: new Date().toString(),
          fields: [
            {
              fieldValue: formik.values.email,
              fieldId: 'email',
              fieldLabel: t('E-Mail'),
            },
            {
              fieldValue: formik.values.salutation,
              fieldId: 'salutation',
              fieldLabel: t('Anrede'),
            },
            {
              fieldValue: formik.values.firstName,
              fieldId: 'firstName',
              fieldLabel: t('Vorname'),
            },
            {
              fieldValue: formik.values.lastName,
              fieldId: 'lastName',
              fieldLabel: t('Nachname'),
            },
          ],
        };

        postNewsletter(blok.rest_api_endpoint, data).catch((error) => {
          console.error(error);
          // We don't set successful to false, as the initial newsletter
          // registration is already submitted and just the rest hook failed
          // setSuccessful(false);
        });
      }
    },
  });

  useEffect(() => {
    if (isLoggedIn) {
      formik.setFieldValue('email', email, false);
    }
  }, [hydrated, isLoggedIn, email]);

  const hasEmailError = Boolean(formik.touched.email && formik.errors.email);

  return (
    <div
      className={classNames(classes.newsletterTile, successful && classes.successful)}
      {...storyblokEditable(blok)}
      id={blok.anchor || undefined}
    >
      <div className={classNames(classes.layout)}>
        <div className={classNames(classes.content)}>
          <div>
            <span className={classes.subHeading}>{t('SWD Newsletter')}</span>
            <div className={classNames('font-display-4', classes.headline)}>{blok.headline}</div>
          </div>
          <RichText text={blok.text} />
          <BulletpointsField list={blok.benefits} />
        </div>
        <div className={classNames(classes.form)}>
          {successful ? (
            <InlineAlert
              severity="success"
              variant="filled"
              size="medium"
              headline={blok.success_headline}
              className={classes.successAlert}
              icon={<MailOutlineFilledIcon aria-hidden />}
            >
              <RichText
                className={classes.successBody}
                text={withBoldEmailInRichtext(blok.success_text, submittedEmail)}
                darkBackground
              />
              {!isEmailLocked && (
                <InlineTextLink
                  buttonType="button"
                  suppressArrowIcon
                  onClick={resetToInitial}
                  className={classes.successResetLink}
                >
                  {t('Andere E-Mail Adresse verwenden')}
                </InlineTextLink>
              )}
            </InlineAlert>
          ) : (
            <>
              <form onSubmit={formik.handleSubmit} id={blok.formId} noValidate data-testid="news-letter-form">
                {blok.showExtendedForm && (
                  <>
                    <FormControl className={classNames(classes.input, 'sizeLarge')}>
                      <FormSelect
                        density="large"
                        displayEmpty={true}
                        error={Boolean(formik.touched.salutation && formik.errors.salutation)}
                        id="salutation"
                        inputProps={{ 'data-testid': 'news-letter-form-salutation-select-input' }}
                        label={<Label text={t('salutation')} />}
                        labelId="salutationLabel"
                        name="salutation"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        options={[
                          {
                            'data-testid': 'news-letter-form-salutation-select-item-mr',
                            label: t('mr'),
                            value: t('mr'),
                          },
                          {
                            'data-testid': 'news-letter-form-salutation-select-item-mrs',
                            label: t('mrs'),
                            value: t('mrs'),
                          },
                          {
                            'data-testid': 'news-letter-form-salutation-select-item-diverse',
                            label: t('diverse'),
                            value: t('diverse'),
                          },
                          {
                            'data-testid': 'news-letter-form-salutation-select-item-none',
                            label: t('none'),
                            value: t('none'),
                          },
                        ]}
                        size="medium"
                        value={formik.values.salutation}
                      />
                    </FormControl>
                    <TextField
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                      helperText={formik.touched.firstName && formik.errors.firstName}
                      name="firstName"
                      id="firstName"
                      className={classNames(classes.input, 'sizeLarge')}
                      label={<Label text={`${t('firstName')}`} />}
                      inputProps={{ 'data-testid': 'news-letter-form-firstName-input' }}
                    />

                    <TextField
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                      helperText={formik.touched.lastName && formik.errors.lastName}
                      name="lastName"
                      id="lastName"
                      className={classNames(classes.input, 'sizeLarge')}
                      label={<Label text={`${t('lastName')}`} />}
                      inputProps={{ 'data-testid': 'news-letter-form-lastName-input' }}
                    />
                  </>
                )}

                <HoneypotField
                  id={HONEYPOT_FIELD}
                  name={HONEYPOT_FIELD}
                  label={t(HONEYPOT_FIELD)}
                  value={formik.values.birthdate}
                  onChange={formik.handleChange}
                />

                <TextField
                  name="email"
                  id="email"
                  type="email"
                  className={classNames(classes.input, 'sizeLarge', !blok.showExtendedForm ? 'hideAsterisk' : '')}
                  label={<Label text={blok.textfield_label} />}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={hasEmailError}
                  helperText={hasEmailError && formik.errors.email}
                  value={formik.values.email}
                  InputProps={{
                    endAdornment: isEmailLocked ? <MarketingOptInInfoIconModal /> : undefined,
                  }}
                  inputProps={{ 'data-testid': 'news-letter-form-email-input' }}
                  disabled={isEmailLocked}
                  required
                  data-theme="magenta"
                />
                <Button
                  data-testid="news-letter-form-submit-button"
                  variant="ctaFull"
                  className={classes.button}
                  type="submit"
                  size="large"
                  data-theme="magenta"
                  disabled={hasEmailError}
                >
                  {blok.button_label}
                </Button>
              </form>

              <small className={classNames('font-copy-small', hasEmailError && classes.optInTextError)}>
                <RichText text={settings?.cdc_marketing_optin_text} />
              </small>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterTile;
